import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { FenceService } from '../../../../services/configurators-2d/applications/fence.service';
import { Board } from 'src/app/models/configurators-2d/applications/fence/board';
import { forkJoin, from, Observable, of, switchMap, tap } from 'rxjs';
import { Configuration } from '../../../../models/configurators-2d/applications/fence/configuration';
import { distinct } from '../../../../services/helpers/utils';
import { KeyValue } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class BoardStateService {
  /**
   * Liste des types de lame
   * number: installationId
   * Board[]: liste des types de lame pour cette installation
   */
  boardByInstallations: WritableSignal<Map<number, Board[]>> = signal(new Map<number, Board[]>());

  /**
   * Indique si le service est entrain de faire des changements
   */
  loading: WritableSignal<boolean> = signal(false);

  private readonly fenceService = inject(FenceService);

  /**
   * Requête l'API et defini la liste des types de lame
   * @param configuration
   */
  setList(configuration: Configuration): Observable<Map<number, Board[]>> {
    this.loading.set(true);

    const installationIds = configuration.configurationInstallations.map(ci => ci.installationId);
    const heights = distinct(configuration.sides.map(s => s.sections.map(sec => sec.height)).flat()) as number[];
    const hasNonRightAngle = configuration.sides.every(s => s.sections.every(sec => sec.hasNonRightAngle));

    const requests: Observable<KeyValue<number, Board[]>>[] = [];

    for (const installationId of installationIds) {
      const otherInstallationIds = installationIds.filter(id => id !== installationId);

      requests.push(
        from(
          this.fenceService.getBoards(
            installationId,
            otherInstallationIds,
            heights,
            configuration.location.id,
            configuration.wallHeight,
            hasNonRightAngle
          )
        )
      );
    }

    return forkJoin(requests).pipe(
      switchMap((results: KeyValue<number, Board[]>[]) => {
        const boardByInstallations = new Map<number, Board[]>();

        for (const result of results) {
          boardByInstallations.set(result.key, result.value);
        }

        this.boardByInstallations.set(boardByInstallations);
        this.loading.set(false);
        return of(boardByInstallations);
      })
    );
  }

  getBoardByInstallation(installationId: number): Board[] {
    return this.boardByInstallations().get(installationId);
  }
}
